import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { genresData } from "../../data/genresData";
import { genresZh } from "../../data/genresTranslations";
import LoadCompleteImage from "../LoadCompleteImage";
import "./GenrePage.css";

const preloadGenreImages = (subgenres) => {
  if (typeof window === "undefined") return Promise.resolve([]);

  const imageUrls = [
    ...new Set(subgenres.map((subgenre) => `/images/subgenres/${subgenre.id}.jpg`)),
  ];

  return Promise.all(
    imageUrls.map(
      (src) =>
        new Promise((resolve) => {
          const image = new Image();
          image.onload = () => resolve({ src, loaded: true });
          image.onerror = () => resolve({ src, loaded: false });
          image.src = src;

          if (image.complete) {
            resolve({ src, loaded: image.naturalWidth > 0 });
          }
        })
    )
  );
};

const GenrePage = ({ language = "EN" }) => {
  const [animationReady, setAnimationReady] = useState(false);
  const [genreImagesReady, setGenreImagesReady] = useState(false);
  const [loadingLanguageSwitch, setLoadingLanguageSwitch] = useState(false);
  const isFirstLanguageRender = useRef(true);

  // 每个子类型的版权信息映射
  const copyrightMap = {
    "documentary-photography":
      "Image © Robert Frank, 1955. Parade, Hoboken, New Jersey. From The Americans. Courtesy Pace/MacGill.",
    "street-photography":
      "Image © Joel Meyerowitz, 1967. Paris street scene. Courtesy of Howard Greenberg Gallery.",
    "war-photography":
      "Image © Anefo/Nationaal Archief, March 1942. Crew members aboard the Dutch minesweeper Hr. Ms. Maria R. Ommering. Public domain via Nationaal Archief.",
    "social-documentary":
      "Image © Mary Ellen Mark, 1983. Erin Blackwell, known as “Tiny,” Pike Street, Seattle. Courtesy of The Mary Ellen Mark Foundation.",
    "conceptual-photography":
      "Image © Victor Burgin, 2013. Mirror Lake. Courtesy of the artist and Cristin Tierney, New York.",
    "staged-tableau-photography":
      "Image © Jeff Wall, 1978. The Destroyed Room. Collection of the National Gallery of Canada, Ottawa.",
    "surreal-photography":
      "Image © Lee Miller Archives, 1930. Untitled [Man and tar], Paris, France.",
    photomontage:
      "Wangechi Mutu, Yo Mama, 2003. Photo © Robert Edemeyer. Courtesy of the artist and Vielmetter Los Angeles.",
    "self-portraiture":
      "Image © Cindy Sherman, 1978. Untitled Film Still #17. Courtesy of the artist and Metro Pictures, New York.",
    "feminist-photography":
      "Image © Nan Goldin, 1991. Gina at Bruce’s Dinner Party, New York City.",
    "postmodernism-in-photography":
      "Image © Andreas Gursky/Artists Rights Society (ARS), New York/VG Bild-Kunst, Bonn",
    "film-based-experiments":
      "Image © Viviane Sassen, 2017. Eudocimus Ruber, from the series 'Of Mud and Lotus.' Courtesy Stevenson (Johannesburg/Cape Town/Amsterdam).",
    "instant-polaroid":
      "Image © Guy Bourdin, 1978. Advertisement for Charles Jourdan. © The Guy Bourdin Estate, 2017. Courtesy of Louise Alexander Gallery.",
    "i-novel-photography":
      "Image © Nobuyoshi Araki, 2017. Untitled (Flower Cemetery). Courtesy of the artist.",
    "classic-landscape":
      "Image © Ansel Adams, 1942. The Tetons and the Snake River, Grand Teton National Park, Wyoming. Public domain, courtesy of the U.S. National Archives",
    "new-topographics":
      "Image © Stephen Shore, 1979. Merced River, Yosemite National Park, California, August 13. Courtesy of 303 Gallery, New York.",
    "urban-landscape":
      "Image © Thomas Struth, 2001. Dallas Parking Lot, Dallas. Gift of Donna and Howard Stone. Collection of the Art Institute of Chicago.",
    "fashion-photography":
      "Image © Guy Bourdin, late 1970s. Advertisement for Charles Jourdan. Courtesy of The Guy Bourdin Estate",
    "advertising-photography":
      "Image © Luca Bruno/AP, 1996. Billboard in Milan, Italy, showing Oliviero Toscani’s United Colors of Benetton campaign poster.",
    "product-photography":
      "Image © Hiro, ca. 1982. Foot Series #8, with Ant, New York.",
    "domestic-family-photography":
      "Image © Tina Barney, 1986. The Birthday Party. Courtesy of the artist and Janet Borden, Inc., New York.",
    "ai-cgi-photography":
      "Image © Dada Projects (Christina Worner), 2021. London Fashion Week teaser.",
  };

  const allSubgenres = useMemo(
    () =>
      genresData.flatMap((genre) =>
        genre.subgenres.map((subgenre) => ({
          ...subgenre,
          parentGenreDescription: genre.description, // Add parent genre description for subgenre card
          parentGenreId: genre.id,
        }))
      ),
    []
  );

  useEffect(() => {
    let cancelled = false;
    setGenreImagesReady(false);

    preloadGenreImages(allSubgenres).then((results) => {
      const failedImages = results.filter((result) => !result.loaded);
      if (failedImages.length > 0) {
        console.warn(
          "Some genre images failed to preload:",
          failedImages.map((result) => result.src)
        );
      }

      if (!cancelled) {
        setGenreImagesReady(true);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [allSubgenres]);

  useEffect(() => {
    if (isFirstLanguageRender.current) {
      isFirstLanguageRender.current = false;
      return;
    }
    setLoadingLanguageSwitch(true);
    setAnimationReady(false);
    const timer = setTimeout(() => {
      setLoadingLanguageSwitch(false);
    }, 420);
    return () => clearTimeout(timer);
  }, [language]);

  useEffect(() => {
    if (loadingLanguageSwitch || !genreImagesReady) return;
    const timer = setTimeout(() => {
      setAnimationReady(true);
    }, 100);
    return () => clearTimeout(timer);
  }, [genreImagesReady, loadingLanguageSwitch, language]);

  const isPageLoading = !genreImagesReady || loadingLanguageSwitch;

  return (
    <div className={`genre-page ${language === "CN" ? "genre-cn" : ""}`}>
      {isPageLoading ? (
        <div className="genre-loading-container">
          <div className="genre-loading-animation">
            <div className="genre-loading-dots">
              <div className="genre-dot"></div>
              <div className="genre-dot"></div>
              <div className="genre-dot"></div>
            </div>
            <p className="genre-loading-text">
              {language === "CN"
                ? "正在加载摄影风格..."
                : "Loading photography genres..."}
            </p>
          </div>
        </div>
      ) : (
        <>
          <div className="page-header">
            <h1>
              {language === "CN" ? "摄影子类型" : "PHOTOGRAPHY SUBGENRES"}
            </h1>
            <p className="intro-text">
              {language === "CN"
                ? "通过不同的摄影子类型探索多元的影像世界。每一种子类型都代表了一种独特的观看与表达方式。"
                : "Explore the diverse world of photography through its various subgenres. Each subgenre represents a unique approach to capturing and interpreting the world around us."}
            </p>
          </div>

          <div
            className={`subgenres-grid ${animationReady ? "animation-ready" : ""}`}
          >
            {allSubgenres.map((subgenre, index) => (
              <div
                key={subgenre.id}
                className="subgenre-card"
                style={{
                  animationDelay: animationReady
                    ? `${Math.floor(index / 3) * 0.1 + (index % 3) * 0.05}s`
                    : "0s",
                }}
              >
                <Link
                  to={`/genres/${subgenre.id}`}
                  className="subgenre-image-link"
                  aria-label={`${
                    language === "CN"
                      ? genresZh.subgenres[subgenre.id] || subgenre.title
                      : subgenre.title
                  }${language === "CN" ? "：了解更多" : ": Continue"}`}
                >
                  <div className="subgenre-image-container">
                    <LoadCompleteImage
                      src={`/images/subgenres/${subgenre.id}.jpg`} // Placeholder image path
                      alt={subgenre.title}
                      className="subgenre-image"
                      revealImmediately
                      onError={(e) => {
                        e.target.src =
                          "https://via.placeholder.com/300x200/cccccc/666666?text=Image"; // Fallback
                      }}
                    />
                  </div>
                </Link>
                <div className="subgenre-content">
                  <h3 className="subgenre-title">
                    {language === "CN"
                      ? genresZh.subgenres[subgenre.id] || subgenre.title
                      : subgenre.title}
                  </h3>
                  <p className="subgenre-description">
                    {language === "CN"
                      ? subgenre.description_zh ||
                        genresZh.genres[subgenre.parentGenreId] ||
                        subgenre.parentGenreDescription
                      : subgenre.description ||
                        subgenre.parentGenreDescription}
                  </p>
                  <p className="subgenre-image-copyright">
                    {copyrightMap[subgenre.id] ||
                      "Image © Classic Photography Collection"}
                  </p>
                  <Link
                    to={`/genres/${subgenre.id}`}
                    className="subgenre-continue-link"
                  >
                    {language === "CN" ? "> 了解更多" : "> Continue"}
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default GenrePage;
